// CTSK-509 — data hooks for Notes: one paged list query + create/update/delete
// mutations. The list uses an infinite query so the document/work-item panel
// ("latest 100", no paging UI) and the standalone page ("Load more") share one
// cache shape. Writes go through apiSingle, so the CSRF header travels
// (CTSK-504 lesson).

import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import {
  NOTE_PREFS_DEFAULT,
  type CollectionEnvelope,
  type Note,
  type NoteColor,
  type NoteCreateInput,
  type NoteOrderInput,
  type NotePrefs,
  type NoteUpdateInput,
} from '@captain-tasko/shared';
import { apiList, apiSingle } from '../../lib/api.js';
import { moveRelative, rechunk } from './layout.js';

// CTSK-514: on the /notes wall the list is ordered by the user's saved view.
export type NotesOrdering = { mode: 'manual' } | { mode: 'sorted'; sortKey: NotePrefs['sortKey']; sortDir: NotePrefs['sortDir'] };
export type NotesListParams = { target: string } | { mine: true; ordering?: NotesOrdering };
type NotesPages = InfiniteData<CollectionEnvelope<Note>, string | undefined>;

export const NOTES_ROOT_KEY = ['notes'] as const;

export const NOTE_PREFS_KEY = [...NOTES_ROOT_KEY, 'prefs'] as const;

function orderingQuery(o: NotesOrdering | undefined): Record<string, string> {
  if (!o) return {};
  return o.mode === 'manual' ? { order: 'manual' } : { sort: o.sortKey, dir: o.sortDir };
}

export function notesListKey(params: NotesListParams) {
  if ('target' in params) return [...NOTES_ROOT_KEY, 'list', params.target] as const;
  const o = params.ordering;
  return [...NOTES_ROOT_KEY, 'list', 'mine', o ? (o.mode === 'manual' ? 'manual' : `${o.sortKey}:${o.sortDir}`) : 'default'] as const;
}

export function useNotePrefs(enabled: boolean) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: NOTE_PREFS_KEY,
    queryFn: () => apiSingle<NotePrefs>('/notes/prefs'),
    enabled,
    staleTime: 60_000,
    retry: 0,
  });
  const write = useMutation({
    mutationFn: (next: NotePrefs) => apiSingle<NotePrefs>('/notes/prefs', { method: 'PUT', body: next }),
  });
  const prefs: NotePrefs = q.data ?? NOTE_PREFS_DEFAULT;
  // Optimistic and synchronous: two quick changes (sort key, then direction)
  // build on each other instead of the second overwriting the first.
  function save(patch: Partial<NotePrefs>) {
    const previous = qc.getQueryData<NotePrefs>(NOTE_PREFS_KEY);
    const next = { ...(previous ?? NOTE_PREFS_DEFAULT), ...patch };
    qc.setQueryData(NOTE_PREFS_KEY, next);
    write.mutate(next, {
      onError: () => {
        if (previous) qc.setQueryData(NOTE_PREFS_KEY, previous);
        else qc.removeQueries({ queryKey: NOTE_PREFS_KEY });
      },
    });
  }
  return { prefs, ready: q.isSuccess || q.isError, save };
}

export function useNotesList(params: NotesListParams, enabled: boolean) {
  const q = useInfiniteQuery({
    queryKey: notesListKey(params),
    queryFn: ({ pageParam }) =>
      apiList<Note>('/notes', {
        query: {
          ...('target' in params ? { target: params.target } : { mine: 'true', ...orderingQuery(params.ordering) }),
          cursor: pageParam,
        },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.page?.nextCursor ?? undefined,
    enabled,
    placeholderData: (prev) => prev, // no blank flash when the sort/order changes
    staleTime: 15_000,
    retry: 0,
  });
  const notes = (q.data?.pages ?? []).flatMap((p) => p.data);
  return {
    notes,
    isLoading: q.isPending && q.fetchStatus !== 'idle',
    isError: q.isError,
    hasMore: q.hasNextPage ?? false,
    isFetchingMore: q.isFetchingNextPage,
    fetchMore: () => void q.fetchNextPage(),
  };
}

function mapPages(data: NotesPages | undefined, fn: (notes: Note[]) => Note[]): NotesPages | undefined {
  if (!data) return data;
  return { ...data, pages: data.pages.map((p) => ({ ...p, data: fn(p.data) })) };
}

export function useNoteMutations(params: NotesListParams) {
  const qc = useQueryClient();
  const key = notesListKey(params);
  const refresh = () => qc.invalidateQueries({ queryKey: NOTES_ROOT_KEY });

  const create = useMutation({
    mutationFn: (input: NoteCreateInput) => apiSingle<Note>('/notes', { method: 'POST', body: input }),
    onSuccess: refresh,
  });

  // Optimistic: the edit shows immediately, rolls back if the server refuses,
  // and always settles against the server's copy.
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: NoteUpdateInput }) =>
      apiSingle<Note>(`/notes/${id}`, { method: 'PATCH', body: patch }),
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<NotesPages>(key);
      qc.setQueryData<NotesPages>(key, (old) =>
        mapPages(old, (notes) =>
          notes.map((n) =>
            n.id === id
              ? {
                  ...n,
                  ...(patch.body !== undefined && { body: patch.body }),
                  ...(patch.color !== undefined && { color: patch.color as NoteColor }),
                  ...(patch.heightPx !== undefined && { heightPx: patch.heightPx }),
                }
              : n,
          ),
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },
    onSettled: refresh,
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiSingle<{ id: string }>(`/notes/${id}`, { method: 'DELETE' }),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<NotesPages>(key);
      qc.setQueryData<NotesPages>(key, (old) => mapPages(old, (notes) => notes.filter((n) => n.id !== id)));
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },
    onSettled: refresh,
  });

  // CTSK-514: move one note in MY manual order. Optimistic (the wall re-orders
  // at once), rolled back if the server refuses, always settled against the server.
  const reorder = useMutation({
    mutationFn: (input: NoteOrderInput) =>
      apiSingle<{ noteId: string; rank: string; rebalanced: boolean }>('/notes/order', { method: 'PUT', body: input }),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<NotesPages>(key);
      const anchor = input.afterId ?? input.beforeId;
      qc.setQueryData<NotesPages>(key, (old) => {
        if (!old || !anchor) return old;
        const flat = old.pages.flatMap((p) => p.data);
        const moved = moveRelative(flat, input.noteId, anchor, input.afterId ? 'after' : 'before');
        return { ...old, pages: rechunk(moved, old.pages.map((p) => p.data.length)).map((data, i) => ({ ...old.pages[i]!, data })) };
      });
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },
    onSettled: refresh,
  });

  return { create, update, remove, reorder };
}

